import { actionCreatorFactory } from 'typescript-fsa';
import { Competencies, StatisticsTagsByCompetency } from '../reducers/statistics';
import { DashboardEventType } from '../../utils/logger';
import { TimeoutType } from '../../utils/utils';
import { AnalysisType } from '../../types/analysis';
import { CardSelectionInfo, FirebaseRawCardData, StudentResult } from '../../types/cards';
import { RawPageData } from '../../../../its-pages/src';
import { SavedModuleCompletionInfo } from '../../types/module-completion';

export interface SdCreatedParams {
  cid: string;
  sessionId: string;
  logStartTsMs: number;
}

export interface AddNewEventParams {
  data: StudentResult;
  displayImmediately: boolean;
}

export interface StudentResultsParams {
  results?: StudentResult[],  // Deprecated (data from Loki)
  cardResults?: FirebaseRawCardData[],
  lastLogTime: number,
  skillDescriptions?: Competencies;
  skillIdToTags?: StatisticsTagsByCompetency;
}

export interface LoadResultsParams {
  isInitialData: boolean, // If true, this is the first event time loading data (only for loki)
  useFirebase: boolean, // If true load events from firebase, false use loki
  skillId: string | undefined, // If no skill specified load all event, otherwise only load the given skill
  cardType?: AnalysisType, // If not defined fetch all issue type (only valid for firebase loading)
  loadFirebaseEvents?: boolean, // If using firebase, load the events instead of cards (for testing)
}

export interface IssueData {
  skillId: string;
  tagId: string;
  levelId: number;
}

export interface TagConfusionData {
  skillId: string;
  tagIds: string[];
}

export interface TagEventData extends IssueData {
  tagText: string;
}

export interface SkillEventData extends IssueData {
  skillName: string;
}

export interface SkillChange {
  skillId: string;
};

export interface ReportCardChange {
  type: string;
};

export type EventData = IssueData | TagEventData | SkillEventData | TagConfusionData | SkillChange | ReportCardChange;

export interface LogParams {
  type: DashboardEventType;
  data?: EventData;
  time?: number;
}
const action = actionCreatorFactory('STATISTICS_DASHBOARD');

/**
 * Action dispatched by teacher client only to set module/skill.
 * This data will be read be several places
 */
export const setCurrentCompetencyId = action<string>(
  'SET_CURRENT_COMPETENCY_ID'
);

/**
 * Action dispatched by student client only to set module/skill.
 * HACK: Should try to merge with the teacher related action.
 */
export const setStudentCurrentComptencyId = action<string>('SET_CURRENT_COMPETENCY_ID_STUDENT');
export const setCurrentStudentUid = action<string>('SET_CURRENT_STUDENT_UID');
export const addNewResults = action<StudentResultsParams>('ADD_NEW_RESULT');
export const addNewEventForExample = action<AddNewEventParams>('ADD_NEW_EVENT_FOR_EXAMPLE');
export const addNewEvents = action<StudentResult[]>('ADD_NEW_EVENTS');

/** Pass in the user id */
export const getModuleCompletionForUser = action<string>('GET_MODULE_COMPLETION_FOR_USER');
export type SetModuleCompletionPayload = {
  userId: string;
  data: SavedModuleCompletionInfo
};
export const setModuleCompletionForUser = action<SetModuleCompletionPayload>('SET_MODULE_COMPLETION_FOR_USER');

export const startNewClassSession = action<SdCreatedParams>('CREATED');
export const loadStudentDataForDashboard = action<LoadResultsParams>('LOAD_INITIAL_STUDENT_DATA');
export const loadAllEventsForUser = action<string>('LOAD_ALL_USER_EVENTS');
export const sdDisposeRequested = action('DISPOSE_REQUESTED');
export const sdDisposed = action('DISPOSED');
export const sdGetNextBatch = action('GET_NEXT_BATCH');
export const sdSetPollTimeout = action<TimeoutType>('SET_POLL_TIMEOUT');

export const logDashboardEventAction = action<LogParams>('LOG_DASHBOARD_EVENT');


type ExamplePayload = {
  showAnswers: boolean;
  showUsername: boolean;
  cardId: string;
  tagIdsToEmphasize?: string[];
}
export type ShowExamplePayload = {
  eventId: string, 
} & ExamplePayload;
/**
 * Request to display a student example based on the event id.
 * Event id ties to the logged data of a question instance answered by the student, may need to be
 * asynchronously loaded.
 * 
 * Payload is the id of event to be loaded and whether the user response should be shown.
 */
export const loadAndDisplayExampleFromEventId = action<ShowExamplePayload>('LOAD_AND_DISPLAY_EVENT_ID');

export type PickExamplePayload = {
  eventIds: string[],
} & ExamplePayload;
export const pickThenLoadExampleFromEventIds = action<PickExamplePayload>('PICK_THEN_LOAD_EVENT_ID');

export type SaveNewExampleSelectionPayload = {
  cardId: string;
} & Partial<CardSelectionInfo>;

/**
 * write out new information related to generating/showing an example when interacting with a card.
 * that is save the seed and/or event id when looking at an example
 */
export const saveNewExampleSelectionInformation = action<SaveNewExampleSelectionPayload>('SAVE_NEW_EXAMPLE_SELECTION_INFORMATION')

export type GenerateExamplePayload = {
  /**
   * Set of event ids to act as a ignore
   */
  eventIds: string[];
  /**
   * list of tags to indicate the generated example should include them in the answer.
   * Note that just based on the card that has the button for the user to generate an example, the
   * information of whether a particular card is correct or incorrect is unknown, so a tag could be a correct
   * answer and the user didn't pick it or tag a was an incorrect answer and the user picked it.
   */
  tagIds: string[];

  /**
   * If specified, the generated example should be associated with a card
   */
  cardId: string;
};

/**
 * Request to generate a new example question using a given event id as an initial template.
 */
export const generateExampleFromEventId = action<GenerateExamplePayload>('GENERATE_FROM_EVENT_ID');

export const exampleToDisplayGenerated = action<RawPageData>('EXAMPLE_GENERATED');

/**
 * Request to update settings related to randomly picking the displayed example within a dashboard card.
 * (i.e. the button in the example problem to generate new example)
 * 
 * Payload is id of the card
 */
export const updateRandomExampleSettingsForCardId = action<string>('UPDATE_RANDOM_EXAMPLE_SETTINGS');

/**
 * Hide/close the shown example
 */
export const removeDisplayedExample = action('REMOVE_DISPLAYED_EXAMPLE');
